import { Button } from "@/atoms-react/button/Button.react.tsx";
import Column from "@/atoms-react/column/Column.react";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react.tsx";
import InputLabel from "@/atoms-react/input/Input.react.tsx";
import Loader from "@/atoms-react/loader/Loader.react.tsx";
import { Base } from "@/atoms-react/text/Text.react.tsx";
import { DeclarativeIncomeSource } from "@/features/flow/types";
import { isDeclarativeMonthlyIncomSourceValid } from "@/features/register/utils";
import { useQuiz } from "@/features/user/quiz/hooks/use-quiz.ts";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { FlowHeader } from "@/flow/components/flow-header.react";
import { useAppDispatch } from "@/redux/hooks";
import { setIsInPreviousStep } from "@/redux/slices/flowSlice";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

type DeclarativeSourceIncomeStepProps = {
  onValidated?: (output: DeclarativeIncomeSource[]) => void;
  declarativeSourceIncome?: DeclarativeIncomeSource[];
};

export const DeclarativeSourceIncomeStep = ({
  declarativeSourceIncome,
  onValidated
}: DeclarativeSourceIncomeStepProps) => {
  const { t } = useTranslation(["pass"]);
  const { getLanguage } = usei18n();
  const dispatch = useAppDispatch();

  const { SourcesIncomeQuery } = useQuiz();
  const { data: sourceIncomeResponse, isLoading: isLoadingSourcesIncome } = SourcesIncomeQuery(
    getLanguage()
  );

  SourcesIncomeQuery(getLanguage());
  const codeOfOtherIncome = sourceIncomeResponse?.sourceIncomeCodes?.find(
    (item) => item.Nom === "Autres revenus"
  )?.id;

  const [sourceIncome, setSourceIncome] = useState<DeclarativeIncomeSource[] | undefined>(
    declarativeSourceIncome
  );
  const checkSourceOther = (sourceIncome ?? [])?.find(
    (d) => d.source_income_id === codeOfOtherIncome
  );

  const handleCheckboxChange = (id: number, isChecked: boolean) =>
    setSourceIncome((prevState) => {
      const newSourcesIncome = isChecked
        ? [
            ...((prevState as DeclarativeIncomeSource[]) ?? []),
            { source_income_id: id, comment: "" }
          ]
        : ((prevState as DeclarativeIncomeSource[]) ?? []).filter(
            (income) => Number(income.source_income_id) !== id
          );

      return newSourcesIncome;
    });

  const handleCommentChange = (id: number, comment: string) =>
    setSourceIncome((prevState) => {
      const updatedSourcesIncome = (prevState ?? [])?.map((income) =>
        income.source_income_id === Number(id) ? { ...income, comment: comment } : income
      );
      return updatedSourcesIncome;
    });
  function execute() {
    if (!isDeclarativeMonthlyIncomSourceValid(sourceIncome)) {
      toast.error(t("flow.id_control.error.must_enter_fields"));
      return;
    }
    dispatch(setIsInPreviousStep(false));
    onValidated?.(sourceIncome as DeclarativeIncomeSource[]);
  }

  return (
    <>
      <Column>
        <div className="mb-12 grow">
          <FlowHeader
            title={`${t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.incomes.question")} *`}
            description=""
            onPreviousClick={() => dispatch(setIsInPreviousStep(true))}
          />
          {isLoadingSourcesIncome && (
            <div className="mt-4 mb-4 flex flex-1 items-center justify-center">
              <Loader className="text-gray-500" />
            </div>
          )}
          {!isLoadingSourcesIncome && (
            <>
              <div className="my-4 mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
                {sourceIncomeResponse?.sourceIncomeCodes &&
                  sourceIncomeResponse?.sourceIncomeCodes?.map((c) => (
                    <Checkbox
                      key={c.id}
                      value={
                        sourceIncome && sourceIncome.some((sI) => sI.source_income_id === c.id)
                      }
                      onChange={(value) => handleCheckboxChange(c.id, value)}
                    >
                      <Base className="mb-1 cursor-pointer">
                        {getLanguage() === "fr" ? c.Nom : c.NomEn}
                      </Base>
                    </Checkbox>
                  ))}
              </div>
              {sourceIncome &&
                sourceIncome.some((income) => income.source_income_id === codeOfOtherIncome) && (
                  <div className={"mb-4"}>
                    <InputLabel
                      label={t(
                        "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.incomes.responses.others_comment"
                      )}
                      value={
                        sourceIncome?.find(
                          (income) => income.source_income_id === codeOfOtherIncome
                        )?.comment
                      }
                      onChange={(value: string) =>
                        handleCommentChange(codeOfOtherIncome as number, value)
                      }
                      className="mb-1 flex-1 md:mb-0"
                    />
                  </div>
                )}
            </>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-center">
          <DotsNavigation total={8} current={7} />
          <Button
            shortcut="enter"
            onClick={() => execute()}
            disabled={
              checkSourceOther && (!checkSourceOther.comment || checkSourceOther.comment === "")
            }
            icon={<ChevronRightIcon />}
            className="mt-4"
          >
            {t("flow.id_control.input.continue")}
          </Button>
        </div>
      </Column>
    </>
  );
};
